import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { Router } from '@angular/router';
import { map, Subject, takeUntil } from 'rxjs';

import { PrescriptionService } from 'app-service';
import { DialogService } from 'app-shared-ui';
import {
  CitiesList,
  Gender,
  IAppointment,
  IMedication,
  MedicalDegree,
  MedicineFrequency,
  MedicineNotes,
  Util,
} from '@alpha/types';
import { ColumnDefinition, ColumnTemplateType } from 'app-core';
import { MatTableDataSource } from '@angular/material/table';

@Component({
  selector: 'app-appointment-prescription',
  templateUrl: './appointment-prescription.component.html',
  styleUrls: ['./appointment-prescription.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  viewProviders: [DialogService, PrescriptionService],
})
export class AppointmentPrescriptionComponent implements OnInit, OnDestroy {
  public prescriptionData: any[] = [];
  public prescriptionItem: any = null;
  public displayedColumns: Array<ColumnDefinition> = [
    {
      id: '#',
      displayName: '#',
      templateType: ColumnTemplateType.Text,
      requestedWidth: 10,
    },
    {
      id: 'Name',
      displayName: 'Name',
      templateType: ColumnTemplateType.Text,
      requestedWidth: 30,
    },
    {
      id: 'Instruction',
      displayName: 'Instruction',
      templateType: ColumnTemplateType.Text,
      requestedWidth: 50,
    },
  ];
  public dataSource = new MatTableDataSource(this.prescriptionData);
  private readonly ngUnsubscribe: Subject<void> = new Subject<void>();

  public constructor(
    private readonly _cdRef: ChangeDetectorRef,
    private readonly _prescriptionService: PrescriptionService,
    private readonly _route: Router,
  ) {}

  public ngOnInit(): void {
    if (this._route.url.split('/')[2] === 'view') {
      this.getPrescription(this._route.url.split('/')[6].split('?')[0]);
    }
  }

  private getPrescription(appointmentId: string): void {
    this._prescriptionService
      .getPrescription(appointmentId)
      .pipe(
        map((appointment: Partial<IAppointment>) => {
          if (appointment) {
            let responseObj: any = {
              _id: '',
              dateIssued: '',
              diagnosis: '',
              followUpDate: '',
              medicalHistory: '',
              notes: '',
              medications: [],
              doctorName: '',
              degree: '',
              address: '',
              registration: '',
              patientName: '',
              date: '',
              clinicName: '',
              patientAge: null,
              patientGender: null,
              patientWeight: null,
            };
            responseObj.doctorName = `${appointment!.doctorId!.userId?.firstName} ${appointment!.doctorId!.userId?.lastName}`;
            responseObj.patientName = `${appointment!.userId!.firstName} ${appointment!.userId!.lastName}`;
            responseObj.date = appointment!.date;
            appointment!.doctorId!.qualification!.degree.forEach(
              (degree, index) =>
                (responseObj.degree +=
                  MedicalDegree[degree].toString() +
                  (appointment!.doctorId!.qualification!.degree.length - 1 >
                  index
                    ? ', '
                    : '')),
            );
            if (
              appointment!.doctorId!.clinic &&
              appointment!.doctorId!.clinic[0]
            ) {
              responseObj.clinicName = `${appointment!.doctorId!.clinic[0]!.name}`;
              responseObj.address = `${appointment!.doctorId!.clinic[0]!.location.block ? appointment!.doctorId!.clinic[0]!.location.block + ',' : ''} 
                                   ${appointment!.doctorId!.clinic[0]!.location.street ? appointment!.doctorId!.clinic[0]!.location.street + ',' : ''}
                                   ${appointment!.doctorId!.clinic[0]!.location.city ? appointment!.doctorId!.clinic[0]!.location.city + ',' : ''}
                                   ${appointment!.doctorId!.clinic[0]!.location.subDistrict ? CitiesList[appointment!.doctorId!.clinic[0]!.location.subDistrict] + ',' : ''}
                                   ${appointment!.doctorId!.clinic[0]!.location.district ? CitiesList[appointment!.doctorId!.clinic[0]!.location.district] + ',' : ''}
                                   ${appointment!.doctorId!.clinic[0]!.location.state ? appointment!.doctorId!.clinic[0]!.location.state + ',' : ''}
                                   Pin - ${appointment!.doctorId!.clinic[0]!.location.zip}`;
            }
            responseObj._id = appointment!.prescription!._id;
            responseObj.registration =
              appointment!.doctorId!.qualification!.registrationNumber;
            responseObj.dateIssued = appointment!.prescription!.dateIssued;
            responseObj.diagnosis = appointment!.prescription!.diagnosis;
            responseObj.followUpDate = appointment!.prescription!.followUpDate;
            responseObj.notes = appointment!.prescription!.notes;
            if (appointment!.userId!.birthday) {
              responseObj.patientAge = Util.calculateAge(
                new Date(appointment!.userId!.birthday),
              );
            }
            if (appointment!.userId!.gender !== undefined) {
              responseObj.patientGender = Gender[appointment!.userId!.gender];
            }
            if (appointment!.userId!.weight !== undefined) {
              responseObj.patientWeight = appointment!.userId!.weight;
            }
            responseObj.medicalHistory =
              appointment!.prescription!.medicalHistory;
            appointment!.prescription!.medications?.forEach((medicine) => {
              let note = '';
              let earlyMorning = medicine.frequency.includes(
                MedicineFrequency.EarlyMorning,
              );
              let morning = medicine.frequency.includes(
                MedicineFrequency.Morning,
              );
              let afternoon = medicine.frequency.includes(
                MedicineFrequency.Afternoon,
              );
              let evening = medicine.frequency.includes(
                MedicineFrequency.Evening,
              );
              let night = medicine.frequency.includes(MedicineFrequency.Night);
              let midnight = medicine.frequency.includes(
                MedicineFrequency.Midnight,
              );
              let timings = `${earlyMorning ? 'Early Morning ' : morning ? 'Morning ' : afternoon ? 'Afternoon ' : evening ? 'Evening ' : night ? 'Night ' : midnight}`;
              switch (medicine.note) {
                case MedicineNotes.BeforeFood:
                  note = 'Before Food';
                  break;
                case MedicineNotes.AfterFood:
                  note = 'After Food';
                  break;
                case MedicineNotes.WithFood:
                  note = 'With Food';
                  break;

                default:
                  note = 'After Food';
                  break;
              }
              responseObj.medicatioins =
                appointment!.prescription!.medications?.map(
                  (medi: IMedication) => {
                    return {
                      id: medi._id,
                      name: medi.name,
                      duration:
                        medi.duration > 1
                          ? `${medi.duration} Days`
                          : `${medi.duration} Day`,
                      frequency: medi.frequency.map((frq) =>
                        MedicineFrequency[frq]
                          .replace(/(?=[A-Z])/g, ' ')
                          .trim(),
                      ),
                      quantity: medi.quantity,
                      note: MedicineNotes[medi.note]
                        .replace(/(?=[A-Z])/g, ' ')
                        .trim(), // e.g. "After Food", "Before Food"
                      instruction: medi.instructions,
                    };
                  },
                );
              responseObj.medications?.push({
                '#': 1,
                Name: medicine.name,
                Instruction: `${medicine.quantity} unit(s) | ${timings} | For ${medicine.duration} day(s) | ${note}`,
              });
            });

            return responseObj;
          }
        }),
        takeUntil(this.ngUnsubscribe),
      )
      .subscribe((res) => {
        if (res) {
          this.prescriptionItem = {
            appointmentId: res?._id,
            doctorName: res?.doctorName,
            degree: res?.doctorId?.qualification?.degree,
            registration: res?.registration,
            clinicName: res?.clinicName,
            address: res?.address,
            patientName: res?.patientName,
            patientAge: res?.patientAge,
            patientGender: res?.patientGender,
            patientWeight: res?.patientWeight,
            date: res?.date,
            dateIssued: res?.dateIssued,
            medicalHistory: res?.medicalHistory,
            diagnosis: res?.diagnosis,
            followUpDate: res?.followUpDate,
            notes: res?.notes,
            medications: res?.medicatioins,
            _id: '',
          };
          this._cdRef.markForCheck();
        }
      });
  }

  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}
